import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
export type OfferResource = {
  title: string;
  detail: string;
  image: string;
};
type OfferResourcesCarouselProps = {
  items: OfferResource[];
};

// Portadas: son las mismas que usa el carrusel del hero ("Todo lo que incluye H.E.R.").
// Se aplican por título. Además, "Guía de compras + Meal Prep" se reemplaza por "Guía H.E.R." y se suma "H.E.R. Dulce" al final.
const CDN = 'https://cdn.shopify.com/s/files/1/0730/1614/8145/files/';
const R2 = 'https://archivos.simpleymejor.com/';
const GUIA_NAME = 'Guía H·E·R· en cobre y negro.png';
// Como en el hero: se prueban en orden estas direcciones y se usa la primera que carga.
const GUIA_COVERS = [R2 + 'her-guia-cover.png', R2 + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent(GUIA_NAME.normalize('NFD')), R2 + 'her-ebooks/' + encodeURIComponent(GUIA_NAME), R2 + encodeURIComponent('Guía H.E.R. en cobre y negro.png')];
const COVERS: Array<[string, string[]]> = [['Protocolo', [R2 + 'httpsarchivos.simpleymejor.comhercoversher-protocolo-cover.png.png']], ['Recetario', [R2 + 'hercoversher-recetario-cover.png.png']], ['Movement', [CDN + 'S8SNVUZHdzf8t092yIlAK_430x.jpg?v=1780543245']], ['Tracker', [CDN + 'v5gVLGm07qY3WP7hCr5Mp_430x.jpg?v=1780543245']], ['Noche en Calma', [CDN + 'qushb3WzLl3BYpA9n1eL_430x.jpg?v=1780543244']], ['Guía H.E.R.', GUIA_COVERS], ['Dulce', [R2 + 'httpsarchivos.simpleymejor.comhercoversher-dulce-cover.png.png']]];
type Slide = {
  title: string;
  imgs: string[];
};
const coversFor = (item: OfferResource): string[] => COVERS.find(([key]) => item.title.includes(key))?.[1] ?? [item.image];
const buildSlides = (items: OfferResource[]): Slide[] => {
  const list: OfferResource[] = items.map(item => item.title.includes('Guía de compras') ? {
    title: 'Guía H.E.R.',
    detail: '',
    image: ''
  } : item);
  if (!list.some(item => item.title.includes('Dulce'))) list.push({
    title: 'H.E.R. Dulce',
    detail: '',
    image: ''
  });
  return list.map(item => ({
    title: item.title,
    imgs: coversFor(item)
  }));
};

// Imagen con respaldo: si una dirección no carga, prueba la siguiente; si ninguna carga, muestra el título.
function CoverImage({
  slide
}: {
  slide: Slide;
}) {
  const [i, setI] = useState(0);
  if (i >= slide.imgs.length) {
    return <div className="flex h-full w-full items-center justify-center bg-[#090807] px-4 text-center font-display text-[18px] text-[#C8A870]" data-id="element-553" data-mp="2l4jy1r1s5o47">
        {slide.title}
      </div>;
  }
  return <img src={slide.imgs[i]} alt={slide.title} loading="eager" decoding="async" onError={() => setI(n => n + 1)} className="h-full w-full object-cover" data-id="element-554" data-mp="3dutmovqqgc4b" />;
}
const AUTOPLAY_DELAY = 3500;
const INTERACTION_PAUSE = 8000;
const SWIPE_THRESHOLD = 40;
export function OfferResourcesCarousel({
  items
}: OfferResourcesCarouselProps) {
  const slides = buildSlides(items);
  const [activeIndex, setActiveIndex] = useState(0);
  const pauseUntilRef = useRef(0);
  const pointerStartXRef = useRef<number | null>(null);
  const pauseAutoplay = () => {
    pauseUntilRef.current = Date.now() + INTERACTION_PAUSE;
  };
  const goTo = (index: number) => {
    pauseAutoplay();
    setActiveIndex(index);
  };
  const moveBy = (direction: 1 | -1) => {
    pauseAutoplay();
    setActiveIndex(current => (current + direction + slides.length) % slides.length);
  };
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (Date.now() >= pauseUntilRef.current) {
        setActiveIndex(current => (current + 1) % slides.length);
      }
    }, AUTOPLAY_DELAY);
    return () => window.clearInterval(timer);
  }, [slides.length]);
  if (slides.length === 0) return null;
  const activeItem = slides[activeIndex];
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary) return;
    pauseAutoplay();
    pointerStartXRef.current = event.clientX;
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartXRef.current === null) return;
    const distance = event.clientX - pointerStartXRef.current;
    pointerStartXRef.current = null;
    if (Math.abs(distance) >= SWIPE_THRESHOLD) {
      moveBy(distance < 0 ? 1 : -1);
    }
  };
  return <div className="text-center" aria-roledescription="carrusel" aria-label="Recursos incluidos en H.E.R." data-id="element-555" data-mp="3rol5i2n2ioj1">
      <div className="relative touch-pan-y" onPointerDown={handlePointerDown} onPointerUp={handlePointerUp} onPointerCancel={() => {
      pointerStartXRef.current = null;
    }} data-id="element-556" data-mp="gytnmq73fr3">
        <button type="button" onClick={() => moveBy(-1)} aria-label="Recurso anterior" className="absolute left-0 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#C8A870]/25 bg-[#0b0907]/70 text-[#C8A870] backdrop-blur-sm transition-colors duration-200 hover:bg-[#C8A870]/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-557" data-mp="9j85qh143h6l">‹</button>

        <div className="relative mx-auto min-h-[248px] w-[176px]" data-id="element-558" data-mp="28q6n6nnu7v1x">
          <AnimatePresence mode="wait" initial={false} data-id="element-559" data-mp="23yons41gq2j2">
            <motion.article key={activeItem.title} initial={{
            opacity: 0,
            scale: 0.97
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.97
          }} transition={{
            duration: 0.22,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute inset-0" aria-live="polite" data-id="element-560" data-mp="23x3v6jypktnu">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-[8px] border border-[#C8A870]/25 bg-[#090807] shadow-[0_18px_40px_-16px_rgba(0,0,0,0.65)]" data-id="element-561" data-mp="2mpoaeylhj096">
                <CoverImage slide={activeItem} data-id="element-562" data-mp="2cpp12zh8r5v9" />
              </div>
              <p className="mt-3 font-display text-[15px] leading-tight text-[#F0E8D8]/85" data-id="element-563" data-mp="16aenbthbrmu2">
                {activeItem.title}
              </p>
            </motion.article>
          </AnimatePresence>
        </div>

        <button type="button" onClick={() => moveBy(1)} aria-label="Recurso siguiente" className="absolute right-0 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[#C8A870]/25 bg-[#0b0907]/70 text-[#C8A870] backdrop-blur-sm transition-colors duration-200 hover:bg-[#C8A870]/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-564" data-mp="3ph8vny1yyxpa">›</button>
      </div>

      <div className="mt-4 flex items-center justify-center gap-2" aria-label="Elegir recurso" data-id="element-565" data-mp="35sgjm1r7tt94">
        {slides.map((item, index) => {
        const isActive = index === activeIndex;
        return <button key={item.title} type="button" onClick={() => goTo(index)} aria-label={`Ver ${item.title}`} aria-current={isActive ? 'true' : undefined} className="flex h-6 items-center justify-center rounded-sm px-0.5 focus:outline-none focus-visible:ring-1 focus-visible:ring-gold" data-id="element-566" data-mp="2wtmka3mi2orv" data-mpi={index}>
              <span className="block h-1.5 rounded-full transition-[width,background-color] duration-200 ease-out" style={{
            width: isActive ? '18px' : '6px',
            background: isActive ? '#C8A870' : 'rgba(255,255,255,0.22)'
          }} data-id="element-567" data-mp="2ru9y7l3gdlqc" />
            </button>;
      })}
      </div>
    </div>;
}